
import React from "react";
import { Layout } from "@/components/layout/Layout";
import { Map, Calendar, Check, Clock, CalendarDays } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Roadmap milestones for the user view
const MILESTONES = [
  { 
    id: 1, 
    title: "Neue UX für Dashboard", 
    description: "Verbesserung der Benutzeroberfläche für bessere Nutzererfahrung", 
    status: "completed", 
    date: "August 2023",
    details: "Unsere neue Benutzeroberfläche wurde komplett überarbeitet, um Ihnen einen besseren Überblick über Ihre Immobilieninvestitionen zu geben und die Navigation zu verbessern."
  },
  { 
    id: 2, 
    title: "AI Chat Integration", 
    description: "Integration eines KI-Chatbots zur Unterstützung der Kunden", 
    status: "in-progress", 
    date: "November 2023",
    details: "Unser KI-Assistent wird Ihnen in Echtzeit bei Fragen zu Immobilien, Investitionen und unserer Plattform helfen können. Die Entwicklung läuft nach Plan und wir testen bereits erste Funktionen intern."
  },
  { 
    id: 3, 
    title: "Mobile App für iOS", 
    description: "Entwicklung einer nativen iOS-App für unsere Kunden", 
    status: "planned", 
    date: "Q1 2024",
    details: "Die mobile App wird alle wichtigen Funktionen unserer Web-Plattform enthalten und zusätzlich standortbezogene Dienste und Push-Benachrichtigungen für neue Immobilienangebote bieten."
  },
  { 
    id: 4, 
    title: "Erweiterte Analyse-Tools", 
    description: "Hinzufügen fortschrittlicher Analysetools für Immobilieninvestoren", 
    status: "planned", 
    date: "Q1 2024",
    details: "Mit unseren neuen Analysetools können Sie detaillierte Marktanalysen, Renditeprognosen und Vergleiche zwischen verschiedenen Immobilieninvestitionen durchführen."
  },
  { 
    id: 5, 
    title: "Integration von Zahlungsanbietern", 
    description: "Integration mehrerer Zahlungsanbieter für flexible Zahlungsoptionen", 
    status: "planned", 
    date: "Q2 2024",
    details: "Wir werden mehrere Zahlungsanbieter integrieren, um Ihnen mehr Flexibilität bei der Abwicklung von Transaktionen zu bieten, einschließlich neuer internationaler Zahlungsmethoden."
  },
];

const RoadmapSection = ({ milestones, title, icon: Icon }) => {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-semibold mb-6 flex items-center text-hossTeal">
        <Icon className="mr-3 text-[#41c7f0]" size={20} />
        {title}
      </h2>
      
      <div className="space-y-6">
        {milestones.map(milestone => (
          <div 
            key={milestone.id}
            className="bg-[#081426] border border-gray-800 rounded-lg p-6 hover:border-[#41c7f0]/30 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${
                milestone.status === "completed" 
                  ? "bg-green-500/20" 
                  : milestone.status === "in-progress" 
                    ? "bg-yellow-500/20" 
                    : "bg-blue-500/20"
              }`}>
                {milestone.status === "completed" 
                  ? <Check size={20} className="text-green-500" /> 
                  : milestone.status === "in-progress" 
                    ? <Clock size={20} className="text-yellow-500" /> 
                    : <Calendar size={20} className="text-blue-500" />
                }
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-hossTeal">{milestone.title}</h3>
                <div className="flex items-center text-sm text-gray-400 mt-1">
                  <CalendarDays className="mr-2" size={16} />
                  {milestone.date}
                </div>
                <p className="mt-3 text-gray-300">{milestone.details}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Roadmap = () => {
  const completedMilestones = MILESTONES.filter(m => m.status === "completed");
  const inProgressMilestones = MILESTONES.filter(m => m.status === "in-progress");
  const plannedMilestones = MILESTONES.filter(m => m.status === "planned");
  
  return (
    <Layout>
      <div className="p-6 animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold mb-2 flex items-center text-hossTeal">
            <Map className="mr-3 text-[#41c7f0]" size={24} />
            Produktfahrplan
          </h1>
          <p className="text-gray-600 max-w-3xl">
            Wir verbessern kontinuierlich unsere Plattform, um Ihnen das beste Erlebnis zu bieten. 
            Hier finden Sie einen Überblick über unsere abgeschlossenen, laufenden und geplanten Entwicklungen.
          </p>
        </div>
        
        <div className="bg-[#0c0c0c] border border-gray-800/30 rounded-xl p-6 shadow-lg">
          <Tabs defaultValue="all" className="mb-6">
            <TabsList className="bg-[#081426] border border-gray-700">
              <TabsTrigger value="all">Alle</TabsTrigger>
              <TabsTrigger value="completed">Abgeschlossen</TabsTrigger>
              <TabsTrigger value="in-progress">In Arbeit</TabsTrigger>
              <TabsTrigger value="planned">Geplant</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-6">
              {inProgressMilestones.length > 0 && (
                <RoadmapSection 
                  milestones={inProgressMilestones} 
                  title="In Arbeit" 
                  icon={Clock} 
                />
              )}
              
              {plannedMilestones.length > 0 && (
                <RoadmapSection 
                  milestones={plannedMilestones} 
                  title="Geplant" 
                  icon={Calendar} 
                />
              )}
              
              {completedMilestones.length > 0 && (
                <RoadmapSection 
                  milestones={completedMilestones} 
                  title="Abgeschlossen" 
                  icon={Check} 
                />
              )}
            </TabsContent>
            
            <TabsContent value="completed">
              {completedMilestones.length > 0 ? (
                <RoadmapSection 
                  milestones={completedMilestones} 
                  title="Abgeschlossen" 
                  icon={Check} 
                />
              ) : (
                <p className="text-gray-400 py-8 text-center">Keine abgeschlossenen Meilensteine gefunden.</p>
              )}
            </TabsContent>
            
            <TabsContent value="in-progress">
              {inProgressMilestones.length > 0 ? (
                <RoadmapSection 
                  milestones={inProgressMilestones} 
                  title="In Arbeit" 
                  icon={Clock} 
                />
              ) : (
                <p className="text-gray-400 py-8 text-center">Keine laufenden Meilensteine gefunden.</p>
              )}
            </TabsContent>
            
            <TabsContent value="planned">
              {plannedMilestones.length > 0 ? (
                <RoadmapSection 
                  milestones={plannedMilestones} 
                  title="Geplant" 
                  icon={Calendar} 
                />
              ) : (
                <p className="text-gray-400 py-8 text-center">Keine geplanten Meilensteine gefunden.</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default Roadmap;
